import React, { useRef, useState, useEffect } from 'react';

const FeaturedItemsDropdown = ({ label, items }) => {
    const dropdownRef = useRef(null);
    const containerRef = useRef(null);
    const linkRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    const openDropdown = () => {
        if (isOpen) return;
        setIsOpen(true);
        dropdownRef.current?.classList.add('show-featured-dropdown');
    };

    const closeDropdown = () => {
        setIsOpen(false);
        dropdownRef.current?.classList.remove('show-featured-dropdown');
    };

    const handleMouseLeave = (event) => {
        if (event.relatedTarget instanceof Node &&
            (dropdownRef.current?.contains(event.relatedTarget) ||
             containerRef.current?.contains(event.relatedTarget))) {
            return;
        }
        closeDropdown();
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isOpen && event.key === 'Escape') {
                event.preventDefault();
                closeDropdown();
                linkRef.current?.focus();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen]);

    if (!items || items.length === 0) {
        return null;
    }

    return (
        <li className="navbar__item navbar__item--level1 navbar__item--featured" ref={containerRef}>
            <a
                ref={linkRef}
                className="navbar__link featured-items__link"
                href="#"
                onClick={(e) => e.preventDefault()}
                onMouseEnter={openDropdown}
                onMouseLeave={handleMouseLeave}
                onFocus={openDropdown}
                aria-expanded={isOpen}
                aria-haspopup="true"
                tabIndex={0}
            >
                <div className="content-link">{label}</div>
            </a>
            <div
                className="featured-items__dropdown"
                ref={dropdownRef}
                onMouseLeave={closeDropdown}
                aria-hidden={!isOpen}
            >
                <div className="featured-items__container scroll-container-parent scroll-container">
                    <div className="featured-items__list">
                        {items.map((item, index) => (
                            <a
                                key={index}
                                href={item.link?.href || '#'}
                                className="featured-items__item"
                                aria-label={item.name}
                                tabIndex={isOpen ? 0 : -1}
                            >
                                <div className="featured-items__image-wrapper">
                                    {item.imageUrl && (
                                        <img
                                            src={item.imageUrl}
                                            alt={item.name}
                                            className="featured-items__image"
                                            loading="lazy"
                                            draggable="false"
                                        />
                                    )}
                                </div>
                                <span className="featured-items__name">{item.name}</span>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </li>
    );
};

export const MobileFeaturedItemsDropdown = ({ label, items }) => {
    const menuRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleMenu = () => {
        menuRef.current?.classList.toggle('navbar__menu--show');
        setIsExpanded((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isExpanded && menuRef.current && !menuRef.current.contains(event.target)) {
                const toggleButton = event.target.closest('.navbar__icon--menu, .navbar__icon--open');
                if (!toggleButton) {
                    menuRef.current?.classList.remove('navbar__menu--show');
                    setIsExpanded(false);
                }
            }
        };

        if (isExpanded) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [isExpanded]);

    if (!items || items.length === 0) {
        return null;
    }

    return (
        <li className={`navbar__item${isExpanded ? ' navbar__item--submenu-open' : ''}`}>
            <a
                className="navbar__link"
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    toggleMenu();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleMenu();
                    }
                }}
                aria-expanded={isExpanded}
                aria-haspopup="true"
            >
                {label}
            </a>
            <button
                className="navbar__icon--caret-right navbar__icon navbar__icon--open"
                onClick={toggleMenu}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleMenu();
                    }
                }}
                aria-expanded={isExpanded}
                aria-label="Open submenu"
                type="button"
            />
            <div className="navbar__menu" ref={menuRef} aria-label={`${label} submenu`}>
                <ul>
                    <div className="navbar__menu-header">
                        <button
                            className="navbar__icon--caret-left navbar__icon"
                            onClick={toggleMenu}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleMenu();
                                }
                            }}
                            aria-label="Back to parent menu"
                            type="button"
                        />
                        <button
                            className="navbar__title"
                            onClick={toggleMenu}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleMenu();
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: label }}
                            aria-label={`Back to parent menu from ${label}`}
                            type="button"
                        />
                    </div>
                    <li className="featured-items__mobile-list">
                        {items.map((item, index) => (
                            <a
                                key={index}
                                href={item.link?.href || '#'}
                                className="featured-items__mobile-item"
                            >
                                <div className="featured-items__mobile-image-wrapper">
                                    {item.imageUrl && (
                                        <img
                                            src={item.imageUrl}
                                            alt={item.name}
                                            className="featured-items__mobile-image"
                                            loading="lazy"
                                        />
                                    )}
                                </div>
                                <span className="featured-items__mobile-name">{item.name}</span>
                            </a>
                        ))}
                    </li>
                </ul>
            </div>
        </li>
    );
};

export default FeaturedItemsDropdown;
